import type { ReactNode } from 'react'
import { Label } from '@/components/ui/label'

/**
 * Label, control, and at most one of hint or error — the error replaces the hint rather than
 * stacking under it, so a field never grows two lines of small print at once.
 *
 * Shared rather than lead-specific, and it has to stay the only one: a dialog that writes its own
 * label-and-input block is a dialog whose halves line up until the day one of them grows a hint.
 */
export function Field({
  id,
  label,
  required,
  error,
  hint,
  children,
}: {
  id: string
  label: string
  required?: boolean
  error?: string
  hint?: string
  children: ReactNode
}) {
  return (
    // `content-start`: the rows keep their own height instead of sharing out whatever space the
    // grid row has. A field without a hint sits next to one with a hint in the same row, and a
    // stretching grid pushed its label and its control down to fill the difference — which is
    // what made the two halves of a row refuse to line up.
    <div className="grid content-start gap-2">
      <Label htmlFor={id}>
        {label}
        {required && (
          <span className="text-destructive" aria-hidden>
            *
          </span>
        )}
      </Label>
      {children}
      {error ? (
        <p id={`${id}-error`} className="text-destructive text-xs">
          {error}
        </p>
      ) : (
        hint && (
          <p id={`${id}-hint`} className="text-muted-foreground text-xs">
            {hint}
          </p>
        )
      )}
    </div>
  )
}
